import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Clock, CheckSquare, TrendingUp, Info } from 'react-feather';
import { Badge } from '../../components/ui/badge';
import { ProjectBadge } from '../../components/ui/project-badge';
import TaskTypeIcon from '../../components/shared/TaskTypeIcon';
import TaskDueDate from '../../components/shared/TaskDueDate';
import { FeatureTag } from '../../components/ui/feature-tag';
import FeatureTagDetailModal from '../../components/FeatureTags/FeatureTagDetailModal';
import TaskLinks from '../../components/Tasks/TaskLinks';
import TaskManagerDiscussions from '../../components/Manager/TaskManagerDiscussions';
import { FeatherFileSmall } from '../../components/ui/icons';
import './TaskSummaryModal.css';

const TaskSummaryModal = ({ 
  task, 
  isOpen, 
  onClose,
  getTaskStatus,
  getStatusVariant,
  getTaskHistory,
  availableTags = [],
  allTasks = [],
  projectId = null,
  allowCrossProject = false,
  isManager = false,
  onTaskClick = null,
  onAddSubtask = null,
  onTaskUpdate = null,
  onLinksUpdated = null,
  showManagerDiscussions = false,
  showWorkDescriptions = false
}) => {
  const [selectedFeatureTag, setSelectedFeatureTag] = useState(null);
  const [isFeatureTagModalOpen, setIsFeatureTagModalOpen] = useState(false);
  // Process task history for performance chart
  const chartData = useMemo(() => {
    if (!getTaskHistory || !task?.id) return null;
    
    const history = getTaskHistory(task.id);
    if (!history || history.length === 0) return null;

    // Helper function to parse date - handles both Date objects, ISO strings, and localized strings
    const parseDate = (dateValue) => {
      if (!dateValue) return null;
      
      // If it's already a Date object
      if (dateValue instanceof Date) {
        return isNaN(dateValue.getTime()) ? null : dateValue;
      }
      
      // If it's a string, try to parse it
      if (typeof dateValue === 'string') {
        // Try ISO format first
        let date = new Date(dateValue);
        if (!isNaN(date.getTime())) {
          return date;
        }
        
        // Try parsing as localized date string (MM/DD/YYYY or DD/MM/YYYY)
        // This is a fallback for dates that were already converted to locale strings
        const parts = dateValue.split(/[/-]/);
        if (parts.length === 3) {
          // Try US format (MM/DD/YYYY)
          date = new Date(`${parts[2]}-${parts[0]}-${parts[1]}`);
          if (!isNaN(date.getTime())) {
            return date;
          }
          // Try DD/MM/YYYY format
          date = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          if (!isNaN(date.getTime())) {
            return date;
          }
        }
      }
      
      return null;
    };

    // Process and validate history entries
    const validHistory = history
      .map(entry => {
        const dateObj = parseDate(entry.date);
        if (!dateObj) {
          console.warn('Invalid date in task history:', entry.date);
          return null;
        }
        return {
          ...entry,
          dateObj: dateObj,
          dateString: entry.date // Keep original for display
        };
      })
      .filter(entry => entry !== null);

    if (validHistory.length === 0) return null;

    // Sort by date (oldest first)
    const sortedHistory = [...validHistory].sort((a, b) => {
      return a.dateObj - b.dateObj;
    });

    // Calculate cumulative hours
    let cumulativeHours = 0;
    const dataPoints = sortedHistory.map((entry, index) => {
      const hours = parseFloat(entry.hours) || 0;
      cumulativeHours += hours;
      return {
        date: entry.dateObj, // Store Date object for calculations
        dateString: entry.dateString, // Keep original string for display
        hours: hours,
        cumulativeHours: cumulativeHours,
        status: entry.status,
        index: index
      };
    });

    return {
      dataPoints,
      maxHours: Math.max(...dataPoints.map(d => d.hours), 1),
      maxCumulative: Math.max(...dataPoints.map(d => d.cumulativeHours), 1),
      totalDays: dataPoints.length
    };
  }, [task, getTaskHistory]);

  if (!isOpen || !task) return null;

  // All daily work descriptions (notes) captured over the task's lifetime
  const workDescriptionEntries = (showWorkDescriptions && getTaskHistory && task?.id)
    ? (getTaskHistory(task.id) || []).filter(entry => entry && entry.notes && String(entry.notes).trim() !== '')
    : [];

  return createPortal(
    <div className="task-summary-modal-overlay" onClick={onClose}>
      <div className="task-summary-modal" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="task-summary-modal__header">
          <div className="task-summary-modal__title">
            <TaskTypeIcon taskType={task.task_type} />
            <h2>{task.title}</h2>
          </div>
          <button 
            className="task-summary-modal__close"
            onClick={onClose}
            aria-label="Close modal"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="task-summary-modal__content">
          {/* Task Info Section */}
          <div className="task-summary-modal__section">
            <div className="task-summary-modal__info-row">
              {task.display_id && (
                <div className="task-summary-modal__info-item">
                  <span className="task-summary-modal__label">Task ID:</span>
                  <span className="task-summary-modal__task-id">{task.display_id}</span>
                </div>
              )}
              {task.project?.name && (
                <div className="task-summary-modal__info-item">
                  <span className="task-summary-modal__label">Project:</span>
                  <ProjectBadge projectName={task.project.name} />
                </div>
              )}
              {getTaskStatus && getStatusVariant && (
                <div className="task-summary-modal__info-item">
                  <span className="task-summary-modal__label">Status:</span>
                  <Badge variant={getStatusVariant(getTaskStatus(task.id))}>
                    {getTaskStatus(task.id)}
                  </Badge>
                </div>
              )}
              {task.feature_tag && (
                <div className="task-summary-modal__info-item">
                  <span className="task-summary-modal__label">Feature:</span>
                  <div 
                    className="task-summary-modal__feature-badge-wrapper"
                    onClick={(e) => {
                      e.stopPropagation();
                      const fullFeatureTag = availableTags.find(ft => ft.id === task.feature_tag.id) || task.feature_tag;
                      setSelectedFeatureTag(fullFeatureTag);
                      setIsFeatureTagModalOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                    title="Click to view feature details"
                  >
                    <FeatureTag
                      name={task.feature_tag.name}
                      color={task.feature_tag.color || '#6366f1'}
                      size="small"
                    />
                    <Info size={14} className="task-summary-modal__feature-info-icon" />
                  </div>
                </div>
              )}
              {task.feature_tag?.due_date && (
                <div className="task-summary-modal__info-item">
                  <span className="task-summary-modal__label">Due Date:</span>
                  <TaskDueDate dueDate={task.feature_tag.due_date} />
                </div>
              )}
            </div>
          </div>

          {/* Description Section */}
          {task.description && (
            <div className="task-summary-modal__section">
              <h3>Description</h3>
              <p className="task-summary-modal__description">
                {task.description}
              </p>
            </div>
          )}

          {/* Work Descriptions Section - all daily work notes over the task lifetime */}
          {showWorkDescriptions && workDescriptionEntries.length > 0 && (
            <div className="task-summary-modal__section">
              <h3>Work Descriptions ({workDescriptionEntries.length})</h3>
              <div className="task-summary-modal__history-list">
                {workDescriptionEntries.map((entry, index) => {
                  const formattedDate = entry.date instanceof Date && !isNaN(entry.date.getTime())
                    ? entry.date.toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })
                    : typeof entry.date === 'string'
                    ? entry.date
                    : 'N/A';

                  return (
                    <div key={index} className="task-summary-modal__history-item">
                      <div className="task-summary-modal__history-header">
                        <span className="task-summary-modal__history-date">{formattedDate}</span>
                        {entry.hours ? (
                          <div className="task-summary-modal__history-hours">
                            <Clock size={14} />
                            <span>{entry.hours} hours</span>
                          </div>
                        ) : null}
                      </div>
                      <p className="task-summary-modal__description">{entry.notes}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Task Links Section */}
          {task && (
            <div className="task-summary-modal__section">
              <TaskLinks
                task={task}
                allTasks={allTasks}
                projectId={projectId || task.project?.id}
                onTaskClick={onTaskClick || ((linkedTaskId) => {
                  // Find the linked task and close this modal to show it
                  const linkedTask = allTasks.find(t => t.id === linkedTaskId);
                  if (linkedTask) {
                    onClose();
                    // The parent component should handle showing the linked task
                  }
                })}
                isManager={isManager}
                allowCrossProject={allowCrossProject}
                onAddSubtask={onAddSubtask}
                onTaskUpdate={onTaskUpdate}
                onLinksUpdated={onLinksUpdated}
              />
            </div>
          )}

          {/* Stats Section - Only for managers */}
          {isManager && (task.totalHours !== undefined || task.occurrences !== undefined) && (
            <div className="task-summary-modal__section">
              <h3>Statistics</h3>
              <div className="task-summary-modal__stats-grid">
                {task.totalHours !== undefined && (
                  <div className="task-summary-modal__stat-card">
                    <div className="task-summary-modal__stat-icon">
                      <Clock size={24} />
                    </div>
                    <div className="task-summary-modal__stat-content">
                      <span className="task-summary-modal__stat-label">Total Hours</span>
                      <span className="task-summary-modal__stat-value">{task.totalHours}</span>
                    </div>
                  </div>
                )}
                {task.occurrences !== undefined && (
                  <div className="task-summary-modal__stat-card">
                    <div className="task-summary-modal__stat-icon">
                      <CheckSquare size={24} />
                    </div>
                    <div className="task-summary-modal__stat-content">
                      <span className="task-summary-modal__stat-label">Times Worked</span>
                      <span className="task-summary-modal__stat-value">{task.occurrences}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Task Attachments Section */}
          {task.attachments && task.attachments.length > 0 && (
            <div className="task-summary-modal__section">
              <h3>Task Attachments ({task.attachments.length})</h3>
              <ul className="task-summary-modal__attachments-list">
                {task.attachments.map(att => (
                  <li key={att.id} className="task-summary-modal__attachment-item">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="task-summary-modal__attachment-link"
                    >
                      <FeatherFileSmall size={20} />
                      <span>{att.file_name}</span>
                      {att.file_size && (
                        <span className="task-summary-modal__file-size">
                          ({(att.file_size / 1024).toFixed(1)} KB)
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Work Plan Attachments Section */}
          {task.work_plan_attachments && task.work_plan_attachments.length > 0 && (
            <div className="task-summary-modal__section">
              <h3>Work Plan Attachments ({task.work_plan_attachments.length})</h3>
              <ul className="task-summary-modal__attachments-list">
                {task.work_plan_attachments.map(att => (
                  <li key={att.id} className="task-summary-modal__attachment-item">
                    <a
                      href={att.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="task-summary-modal__attachment-link"
                    >
                      <FeatherFileSmall size={20} />
                      <span>{att.file_name}</span>
                      {att.file_size && (
                        <span className="task-summary-modal__file-size">
                          ({(att.file_size / 1024).toFixed(1)} KB)
                        </span>
                      )}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Performance Chart Section - Only for managers */}
          {isManager && chartData && chartData.dataPoints.length > 0 && (
            <div className="task-summary-modal__section">
              <h3>
                <TrendingUp size={18} />
                Performance Over Time
              </h3>
              <div className="task-summary-modal__chart-container">
                <div className="task-summary-modal__chart-header">
                  <div className="task-summary-modal__chart-legend">
                    <div className="task-summary-modal__legend-item">
                      <div className="task-summary-modal__legend-dot daily"></div>
                      <span>Daily Hours</span>
                    </div>
                    <div className="task-summary-modal__legend-item">
                      <div className="task-summary-modal__legend-dot cumulative"></div>
                      <span>Cumulative Hours</span>
                    </div>
                  </div>
                </div>
                <div className="task-summary-modal__chart">
                  <svg 
                    className="task-summary-modal__chart-svg" 
                    viewBox={`0 0 ${Math.max(400, chartData.dataPoints.length * 60)} 220`}
                    preserveAspectRatio="xMidYMid meet"
                  >
                    {/* Grid lines */}
                    <defs>
                      <linearGradient id="areaGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.3" />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0.05" />
                      </linearGradient>
                      <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="#3b82f6" />
                        <stop offset="100%" stopColor="#8b5cf6" />
                      </linearGradient>
                    </defs>
                    
                    {/* Y-axis grid lines - show both daily and cumulative scales */}
                    {[0, 1, 2, 3, 4].map(i => {
                      const y = 40 + (i * 35); // Adjusted spacing: 40, 75, 110, 145, 180
                      const cumulativeValue = (chartData.maxCumulative / 4) * (4 - i);
                      const dailyValue = (chartData.maxHours / 4) * (4 - i);
                      const isZero = i === 4; // Bottom line is 0
                      
                      return (
                        <g key={`grid-${i}`}>
                          <line
                            x1="30"
                            y1={y}
                            x2={Math.max(400, chartData.dataPoints.length * 60) - 20}
                            y2={y}
                            stroke={isZero ? "#d1d5db" : "#e5e7eb"}
                            strokeWidth={isZero ? "2" : "1"}
                            strokeDasharray={isZero ? "none" : "2,2"}
                          />
                          {/* Show cumulative hours on left (for the line) */}
                          <text
                            x="15"
                            y={isZero ? y - 2 : y + 4}
                            textAnchor="end"
                            fontSize="9"
                            fill="#6b7280"
                          >
                            {cumulativeValue.toFixed(1)}h
                          </text>
                          {/* Show daily hours on right (for the bars) */}
                          <text
                            x={Math.max(400, chartData.dataPoints.length * 60) - 15}
                            y={isZero ? y - 2 : y + 4}
                            textAnchor="start"
                            fontSize="9"
                            fill="#8b5cf6"
                            opacity="0.7"
                          >
                            {dailyValue.toFixed(1)}h
                          </text>
                        </g>
                      );
                    })}
                    
                    {/* X-axis - this is the 0 line, already drawn above */}
                    
                    {/* Cumulative hours area */}
                    {chartData.dataPoints.length > 1 && (
                      <path
                        d={`M ${30 + (0 * 60)},${180 - (chartData.dataPoints[0].cumulativeHours / chartData.maxCumulative) * 140} ${chartData.dataPoints.map((point, index) => {
                          const x = 30 + (index * 60);
                          const y = 180 - (point.cumulativeHours / chartData.maxCumulative) * 140;
                          return `L ${x},${y}`;
                        }).join(' ')} L ${30 + ((chartData.dataPoints.length - 1) * 60)},180 L 30,180 Z`}
                        fill="url(#areaGradient)"
                      />
                    )}
                    
                    {/* Cumulative hours line */}
                    {chartData.dataPoints.length > 1 && (
                      <polyline
                        points={chartData.dataPoints.map((point, index) => {
                          const x = 30 + (index * 60);
                          const y = 180 - (point.cumulativeHours / chartData.maxCumulative) * 140;
                          return `${x},${y}`;
                        }).join(' ')}
                        fill="none"
                        stroke="url(#lineGradient)"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    )}
                    
                    {/* Daily hours bars */}
                    {chartData.dataPoints.map((point, index) => {
                      const x = 30 + (index * 60);
                      // Scale bars to use the full chart area height (140px from y=40 to y=180)
                      // But scale relative to maxHours, not maxCumulative, so bars represent daily hours accurately
                      const chartAreaHeight = 140; // From y=40 to y=180
                      const barHeight = chartData.maxHours > 0 
                        ? (point.hours / chartData.maxHours) * chartAreaHeight 
                        : 0;
                      const barY = 180 - barHeight;
                      
                      return (
                        <g key={`bar-${index}`}>
                          <rect
                            x={x - 8}
                            y={barY}
                            width="16"
                            height={barHeight}
                            fill="#8b5cf6"
                            opacity="0.7"
                            rx="2"
                          />
                          {/* Tooltip circle on cumulative line */}
                          <circle
                            cx={x}
                            cy={180 - (point.cumulativeHours / chartData.maxCumulative) * 140}
                            r="3.5"
                            fill="#3b82f6"
                            className="task-summary-modal__chart-point"
                          />
                          {/* Date label */}
                          <text
                            x={x}
                            y="200"
                            textAnchor="middle"
                            fontSize="9"
                            fill="#6b7280"
                            transform={`rotate(-45 ${x} 200)`}
                          >
                            {point.date instanceof Date && !isNaN(point.date.getTime())
                              ? point.date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
                              : point.dateString || 'N/A'}
                          </text>
                        </g>
                      );
                    })}
                  </svg>
                </div>
                <div className="task-summary-modal__chart-stats">
                  <div className="task-summary-modal__chart-stat">
                    <span className="task-summary-modal__chart-stat-label">Avg Daily Hours</span>
                    <span className="task-summary-modal__chart-stat-value">
                      {(chartData.dataPoints.reduce((sum, d) => sum + d.hours, 0) / chartData.dataPoints.length).toFixed(1)}h
                    </span>
                  </div>
                  <div className="task-summary-modal__chart-stat">
                    <span className="task-summary-modal__chart-stat-label">Peak Day</span>
                    <span className="task-summary-modal__chart-stat-value">
                      {Math.max(...chartData.dataPoints.map(d => d.hours)).toFixed(1)}h
                    </span>
                  </div>
                  <div className="task-summary-modal__chart-stat">
                    <span className="task-summary-modal__chart-stat-label">Total Days</span>
                    <span className="task-summary-modal__chart-stat-value">
                      {chartData.totalDays}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Task History Section - Only for managers */}
          {isManager && getTaskHistory && (
            <div className="task-summary-modal__section">
              <h3>Task History</h3>
              <div className="task-summary-modal__history-list">
                {getTaskHistory(task.id).map((entry, index) => {
                  // Format date for display
                  const formattedDate = entry.date instanceof Date && !isNaN(entry.date.getTime())
                    ? entry.date.toLocaleDateString('en-US', { 
                        year: 'numeric', 
                        month: 'short', 
                        day: 'numeric' 
                      })
                    : typeof entry.date === 'string'
                    ? entry.date
                    : 'N/A';
                  
                  return (
                    <div key={index} className="task-summary-modal__history-item">
                      <div className="task-summary-modal__history-header">
                        <span className="task-summary-modal__history-date">{formattedDate}</span>
                        <Badge variant={entry.status === 'Completed' ? 'success' : 'warning'}>
                          {entry.status}
                        </Badge>
                      </div>
                      <div className="task-summary-modal__history-details">
                        <div className="task-summary-modal__history-hours">
                          <Clock size={14} />
                          <span>{entry.hours} hours</span>
                        </div>
                        {entry.notes && (
                          <div className="task-summary-modal__history-notes">
                            <span className="task-summary-modal__notes-label">Notes:</span>
                            <span>{entry.notes}</span>
                          </div>
                        )}
                        {entry.attachments && entry.attachments.length > 0 && (
                          <div className="task-summary-modal__history-attachments">
                            <span className="task-summary-modal__attachments-label">Attachments:</span>
                            <ul className="task-summary-modal__history-attachments-list">
                              {entry.attachments.map(att => (
                                <li key={att.id} className="task-summary-modal__history-attachment-item">
                                  <a
                                    href={att.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="task-summary-modal__history-attachment-link"
                                  >
                                    <FeatherFileSmall size={16} />
                                    <span>{att.file_name}</span>
                                    {att.file_size && (
                                      <span className="task-summary-modal__file-size">
                                        ({(att.file_size / 1024).toFixed(1)} KB)
                                      </span>
                                    )}
                                  </a>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Manager Discussions Section (opt-in, manager only) */}
          {showManagerDiscussions && task?.id && (
            <div className="task-summary-modal__section">
              <TaskManagerDiscussions
                taskId={task.id}
                taskTitle={task.title}
                taskDescription={task.description}
              />
            </div>
          )}

        </div>
      </div>

      {/* Feature Tag Detail Modal */}
      {selectedFeatureTag && (
        <FeatureTagDetailModal
          tag={selectedFeatureTag}
          isOpen={isFeatureTagModalOpen}
          onClose={() => {
            setIsFeatureTagModalOpen(false);
            setSelectedFeatureTag(null);
          }}
          tasks={allTasks.length > 0 
            ? allTasks.filter(t => t.feature_tag?.id === selectedFeatureTag.id)
            : [task].filter(t => t.feature_tag?.id === selectedFeatureTag.id)
          }
          users={[]}
        />
      )}
    </div>,
    document.body
  );
};

export default TaskSummaryModal;

